import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "modal is-active" }
const _hoisted_2 = { class: "modal-card" }
const _hoisted_3 = { class: "modal-card-head" }
const _hoisted_4 = { class: "media" }
const _hoisted_5 = { class: "media-left" }
const _hoisted_6 = { class: "image is-48x48" }
const _hoisted_7 = { class: "app-icon" }
const _hoisted_8 = ["xlink:href"]
const _hoisted_9 = { class: "media-content" }
const _hoisted_10 = { class: "title is-4" }
const _hoisted_11 = { class: "subtitle is-6" }
const _hoisted_12 = { class: "modal-card-body" }
const _hoisted_13 = { class: "columns" }
const _hoisted_14 = { class: "column main-content-view" }
const _hoisted_15 = {
  key: 0,
  class: "tabs"
}
const _hoisted_16 = { class: "column is-one-fifth" }
const _hoisted_17 = { class: "container install-actions" }
const _hoisted_18 = { class: "container app-links" }
const _hoisted_19 = { key: 0 }
const _hoisted_20 = ["href"]
const _hoisted_21 = { key: 1 }
const _hoisted_22 = ["href"]
const _hoisted_23 = { key: 2 }
const _hoisted_24 = ["href"]

import { InstallAppViewModel } from '../models';
import { appsApi } from '../services/apps';
import { AppApiFactory } from '../services/appApiFactory';
import Toast from '@/shared/services/toast';
import { GET_CHANNEL_IDENTITIES } from '@/store/channel-identities/actionTypes';
import AppUninstallNotAllowedModal from './AppUninstallNotAllowedModal.vue';
import appInsights from '@/shared/services/appInsights';
import { ADD_INSTALLED_APP, REMOVE_INSTALLED_APP } from '@/store/app/mutationTypes';
import { useStore } from 'vuex';
import { computed, onBeforeMount, ref } from 'vue';
import { ChannelIdentityViewModel } from '@/messaging/models/channelIdentityViewModel';
import { useRouter } from 'vue-router';


export default /*@__PURE__*/_defineComponent({
  __name: 'AppModal',
  setup(__props) {

const router = useRouter();
const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const currentApp = computed(() => store.getters.currentApp);
const isWorkspaceFeatureEnabled = (feature: string) => computed(() => store.getters.isWorkspaceFeatureEnabled(feature));
const addInstalledApp = (appId: string) => store.commit(ADD_INSTALLED_APP, appId);
const removeInstalledApp = (appId: string) => store.commit(REMOVE_INSTALLED_APP, appId);
const loadChannelIdentities = (forceReload = false): Promise<ChannelIdentityViewModel[]> =>
  store.dispatch(GET_CHANNEL_IDENTITIES, forceReload);

const tabs = ref<string[]>([]);
const activeTab = ref('');
const actionInProgress = ref(false);
const showAppUninstallNotAllowedModal = ref(false);

const uninstallNotAllowedMessage = computed(() => {
  const specificAppApi = AppApiFactory.get(currentApp.value.id);
  return specificAppApi.getUninstallNotAllowedMessage();
});

const eventProperties = computed(() => {
  return {
    appId: currentApp.value.id,
    workspaceId: currentWorkspace.value.id,
  };
});

onBeforeMount(() => {
  if (currentApp.value == null) {
    showAppsStore();
    return;
  }

  if (currentApp.value.isInstalled && currentApp.value.requiresInstallConfig) {
    showAppConfig();
    return;
  }

  showAppOverview();
});

function isActiveTab(tab: string): boolean {
  return activeTab.value === tab;
}

function canInstall(): boolean {
  if (isAppLive()) {
    return true;
  }

  if (isWorkspaceFeatureEnabled(`app_${currentApp.value.id}`)) {
    return true;
  }

  return false;
}

function hasStatusBadge(): boolean {
  return !isAppLive();
}

function getStatusBadgeText(): string {
  if (!isAppLive()) {
    return 'Coming Soon!';
  }

  return '';
}

function installApp(): void {
  appInsights.trackEvent({ name: 'App install clicked' }, eventProperties.value);
  if (!canInstall()) {
    return;
  }

  if (actionInProgress.value) {
    return;
  }

  actionInProgress.value = true;

  const req = {
    appId: currentApp.value.id,
    published: true,
  } as InstallAppViewModel;

  appsApi
    .install(currentWorkspace.value.id, req)
    .then((result) => {
      appInsights.trackEvent({ name: 'App installed' }, eventProperties.value);

      // update apps list
      currentApp.value.isInstalled = true;
      currentApp.value.installedAppId = result.installedAppId;
      if (currentApp.value.requiresInstallConfig) {
        showAppConfig();
      }
      addInstalledApp(currentApp.value.id);
    })
    .finally(() => {
      actionInProgress.value = false;
    });
}

function uninstallApp(): void {
  appInsights.trackEvent({ name: 'App uninstall clicked' }, eventProperties.value);
  if (actionInProgress.value) {
    return;
  }

  actionInProgress.value = true;

  const specificAppApi = AppApiFactory.get(currentApp.value.id);

  if (specificAppApi && !specificAppApi.canUninstall()) {
    showAppUninstallNotAllowedModal.value = true;
    actionInProgress.value = false;
    return;
  }

  let uninstallPromise: Promise<void>;

  if (specificAppApi) {
    uninstallPromise = specificAppApi.uninstall(currentWorkspace.value.id, currentApp.value.installedAppId).then(() => {
      return appsApi.uninstall(currentWorkspace.value.id, currentApp.value.installedAppId);
    });
  } else {
    uninstallPromise = appsApi.uninstall(currentWorkspace.value.id, currentApp.value.installedAppId);
  }

  uninstallPromise // either one or both of the above two promises
    .then(() => {
      appInsights.trackEvent({ name: 'App uninstalled' }, eventProperties.value);

      // update apps list
      currentApp.value.isInstalled = false;
      currentApp.value.installedAppId = '';
      showAppOverview();
      loadChannelIdentities(true);
      removeInstalledApp(currentApp.value.id);
    })
    .catch((err) => {
      Toast.showError(err.message);
    })
    .finally(() => {
      actionInProgress.value = false;
    });
}

function showAppsStore(): void {
  router.push({ name: 'apps' });
}

function showAppOverview(): void {
  activeTab.value = 'Overview';
  router.push({ name: 'app-overview' });
}

async function showAppConfig() {
  activeTab.value = 'Config';
  try {
    await router.push({ name: 'app-config', params: { id: currentApp.value.id } });
  } catch (error: any) {
    console.error('Failed to push to app-config', error);
  }
}

function isAppLive(): boolean {
  return !currentApp.value.status || currentApp.value.status === 'live';
}

return (_ctx: any,_cache: any) => {
  const _component_n_badge = _resolveComponent("n-badge")!
  const _component_router_view = _resolveComponent("router-view")!
  const _component_n_button = _resolveComponent("n-button")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[11] || (_cache[11] = _createElementVNode("div", { class: "modal-background" }, null, -1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("header", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("figure", _hoisted_6, [
              (_openBlock(), _createElementBlock("svg", _hoisted_7, [
                _createElementVNode("use", {
                  "xmlns:xlink": "http://www.w3.org/1999/xlink",
                  "xlink:href": `#${currentApp.value.icon || ''}`
                }, null, 8, _hoisted_8)
              ]))
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("p", _hoisted_10, [
              _createElementVNode("strong", null, _toDisplayString(currentApp.value.name), 1),
              (hasStatusBadge())
                ? (_openBlock(), _createBlock(_component_n_badge, {
                    key: 0,
                    variant: "secondary",
                    class: "status-badge"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(getStatusBadgeText()), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("p", _hoisted_11, _toDisplayString(currentApp.value.category), 1)
          ])
        ]),
        _createElementVNode("button", {
          class: "delete",
          "aria-label": "close",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (showAppsStore()))
        })
      ]),
      _createElementVNode("section", _hoisted_12, [
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("div", _hoisted_14, [
            (currentApp.value.isInstalled && currentApp.value.requiresInstallConfig)
              ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                  _createElementVNode("ul", null, [
                    _createElementVNode("li", {
                      class: _normalizeClass({ 'is-active': isActiveTab('Overview') }),
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (showAppOverview()))
                    }, [...(_cache[6] || (_cache[6] = [
                      _createElementVNode("a", null, "Overview", -1)
                    ]))], 2),
                    _createElementVNode("li", {
                      class: _normalizeClass({ 'is-active': isActiveTab('Config') }),
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (showAppConfig()))
                    }, [...(_cache[7] || (_cache[7] = [
                      _createElementVNode("a", null, "Config", -1)
                    ]))], 2)
                  ])
                ]))
              : _createCommentVNode("", true),
            _createVNode(_component_router_view)
          ]),
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("div", _hoisted_17, [
              (!currentApp.value.isInstalled)
                ? (_openBlock(), _createBlock(_component_n_button, {
                    key: 0,
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (installApp())),
                    variant: "success",
                    disabled: !canInstall(),
                    loading: actionInProgress.value
                  }, {
                    default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                      _createTextVNode(" Install ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled", "loading"]))
                : _createCommentVNode("", true),
              (currentApp.value.isInstalled)
                ? (_openBlock(), _createBlock(_component_n_button, {
                    key: 1,
                    onClick: _cache[4] || (_cache[4] = ($event: any) => (uninstallApp())),
                    variant: "danger",
                    loading: actionInProgress.value
                  }, {
                    default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                      _createTextVNode(" Uninstall ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["loading"]))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_18, [
              _cache[10] || (_cache[10] = _createElementVNode("h6", { class: "title is-6" }, "Related Links", -1)),
              (currentApp.value.websiteUrl)
                ? (_openBlock(), _createElementBlock("p", _hoisted_19, [
                    _createElementVNode("a", {
                      href: currentApp.value.websiteUrl,
                      target: "_blank"
                    }, "Website", 8, _hoisted_20)
                  ]))
                : _createCommentVNode("", true),
              (currentApp.value.privacyPolicyUrl)
                ? (_openBlock(), _createElementBlock("p", _hoisted_21, [
                    _createElementVNode("a", {
                      href: currentApp.value.privacyPolicyUrl,
                      target: "_blank"
                    }, "Privacy Policy", 8, _hoisted_22)
                  ]))
                : _createCommentVNode("", true),
              (currentApp.value.termsOfServiceUrl)
                ? (_openBlock(), _createElementBlock("p", _hoisted_23, [
                    _createElementVNode("a", {
                      href: currentApp.value.termsOfServiceUrl,
                      target: "_blank"
                    }, "Terms of Service", 8, _hoisted_24)
                  ]))
                : _createCommentVNode("", true)
            ])
          ])
        ])
      ])
    ]),
    (showAppUninstallNotAllowedModal.value)
      ? (_openBlock(), _createBlock(AppUninstallNotAllowedModal, {
          key: 0,
          message: uninstallNotAllowedMessage.value,
          onCancel: _cache[5] || (_cache[5] = ($event: any) => (showAppUninstallNotAllowedModal.value = false))
        }, null, 8, ["message"]))
      : _createCommentVNode("", true)
  ]))
}
}

})